import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, ArrowRight, Zap, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import AiGenerationProgress from "@/components/AiGenerationProgress";

const AUDIENCE_LEVELS = ["Analistas", "Ejecutivos de cuenta", "Gerencia media", "Alta dirección"];
const TIMEFRAMES = ["4 semanas", "2 meses", "3-6 meses", "flexible"];

// Bloque 2 · IADA aval question (obligatory on intake)
const IADA_OPTIONS = [
  {
    val: "yes",
    label: "Sí, con aval IADA (+30%)",
    desc: "Emisión conjunta DCC × IADA · Anáhuac Business School.",
    testid: "iada-yes",
  },
  {
    val: "no",
    label: "No, solo aval DCC",
    desc: "Programa con emisión académica DCC · sin costo IADA.",
    testid: "iada-no",
  },
  {
    val: "both",
    label: "Mostrar ambas opciones",
    desc: "La propuesta incluirá comparación de precios con y sin aval.",
    testid: "iada-both",
  },
];

const MIN_LOADER_MS = 5000; // Bloque 2 spec: show animation ≥ 5s even if LLM returns earlier.

export default function AiIntakeStep({ onGenerated, onSkip }) {
  const [searchParams] = useSearchParams();
  const prefillId = searchParams.get("prefill");
  const [aiEnabled, setAiEnabled] = useState(null);
  const [form, setForm] = useState({
    challenge: "",
    industry: "Servicios financieros y bursátiles",
    audience_level: "Gerencia media",
    audience_size: "",
    timeframe: "3-6 meses",
    company: "",
    country: "México",
    measurable_goals: "",
    wants_iada: "both",
  });
  const [loading, setLoading] = useState(false);
  const [finished, setFinished] = useState(false);
  const [prefillLoaded, setPrefillLoaded] = useState(false);

  useEffect(() => {
    api.get("/public/tailor-made/ai-status").then((r) => setAiEnabled(r.data.enabled)).catch(() => setAiEnabled(false));
  }, []);

  useEffect(() => {
    if (!prefillId || prefillLoaded) return;
    api.get(`/public/tailor-made/prefill/${prefillId}`)
      .then((r) => {
        const p = r.data?.intake || {};
        setForm((f) => ({
          ...f,
          challenge: p.challenge || f.challenge,
          industry: p.industry || f.industry,
          audience_level: p.audience_level || f.audience_level,
          audience_size: p.audience_size ? String(p.audience_size) : f.audience_size,
          timeframe: p.timeframe || f.timeframe,
        }));
        setPrefillLoaded(true);
        toast.success("Datos precargados desde el asistente. Revisa y genera la propuesta.");
      })
      .catch(() => { setPrefillLoaded(true); });
  }, [prefillId, prefillLoaded]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.challenge.trim().length < 20) { toast.error("Describe el reto con al menos 20 caracteres."); return; }
    const audienceNum = Number(form.audience_size);
    if (!audienceNum || audienceNum < 1) {
      toast.error("Ingresa un tamaño de audiencia mayor a 0.");
      return;
    }
    setLoading(true);
    setFinished(false);
    track("ai_suggest_requested", {
      challenge_length: form.challenge.length,
      audience_level: form.audience_level,
      audience_size: audienceNum,
      wants_iada: form.wants_iada,
    });
    const startedAt = Date.now();
    try {
      // Exclude wants_iada from the AI payload — backend AI schema doesn't need it,
      // it's stored later when the request is persisted.
      const { wants_iada, ...aiPayload } = form;
      const payload = { ...aiPayload, audience_size: audienceNum };
      const [{ data }] = await Promise.all([
        api.post("/public/tailor-made/ai-suggest", payload, { timeout: 45000 }),
        // Force minimum on-screen duration for the loader (better perceived quality).
        new Promise((res) => setTimeout(res, MIN_LOADER_MS)),
      ]);
      setFinished(true);
      track("ai_suggest_succeeded", {
        modules_suggested: data.content?.suggested_program_ids?.length || 0,
        provider: data.provider, model: data.model,
        elapsed_ms: Date.now() - startedAt,
      });
      // brief pause on the "done" state so the user notices the checkmarks
      await new Promise((res) => setTimeout(res, 350));
      onGenerated({ ...payload, wants_iada: form.wants_iada }, data);
      toast.success("Propuesta generada · edítala en el paso siguiente.");
    } catch (er) {
      const detail = er.response?.data?.detail || er.message;
      track("ai_suggest_failed", { error_type: er.response?.status || "network" });
      if (er.response?.status === 429) toast.error("Demasiadas solicitudes. Espera unos minutos.");
      else if (er.response?.status === 503) toast.error("AI assist temporalmente no disponible.");
      else if (er.response?.status === 422) toast.error("Datos inválidos en el formulario. Revisa los campos.");
      else toast.error(formatApiError(detail));
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="max-w-3xl">
        <AiGenerationProgress finished={finished} wantsIada={form.wants_iada} />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="flex items-start gap-3 p-4 rounded-lg bg-[#F26A21]/10 border border-[#F26A21]/30 mb-6">
        <Sparkles className="w-5 h-5 text-[#F26A21] flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-[#0B0B0B]">Propuesta pre-llenada por IA</p>
          <p className="text-xs text-gray-600 mt-1">Cuéntanos tu reto y generamos un brief completo (5 secciones · ~40 campos). Tú solo revisas y confirmas.</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="ai-intake-form">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">
            Reto de negocio / objetivo *
          </label>
          <textarea rows={4}
            data-testid="ai-challenge"
            value={form.challenge}
            onChange={(e) => setForm({ ...form, challenge: e.target.value })}
            placeholder="Ej: Queremos capacitar a 200 gerentes comerciales de nuestra red de sucursales en originación de crédito PYME y evaluación de riesgo."
            className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
          <p className="mt-1 text-[11px] text-gray-500">{form.challenge.length} / 4000 caracteres</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Industria / sector" val={form.industry} on={(v) => setForm({ ...form, industry: v })} testid="ai-industry" />
          <Select label="Nivel de audiencia *" val={form.audience_level} on={(v) => setForm({ ...form, audience_level: v })}
            options={AUDIENCE_LEVELS} testid="ai-level" />
          <FieldNum label="Tamaño de audiencia *" val={form.audience_size} on={(v) => setForm({ ...form, audience_size: v.replace(/[^0-9]/g, "") })} testid="ai-size" />
          <Select label="Timeframe *" val={form.timeframe} on={(v) => setForm({ ...form, timeframe: v })}
            options={TIMEFRAMES} testid="ai-timeframe" />
          <Field label="Empresa (opcional)" val={form.company} on={(v) => setForm({ ...form, company: v })} testid="ai-company" />
          <Field label="País principal" val={form.country} on={(v) => setForm({ ...form, country: v })} testid="ai-country" />
        </div>

        <div>
          <label className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">
            Objetivos medibles (opcional)
          </label>
          <textarea rows={2}
            data-testid="ai-goals"
            value={form.measurable_goals}
            onChange={(e) => setForm({ ...form, measurable_goals: e.target.value })}
            placeholder="Ej: Aumentar aprobación de crédito PYME 15% en 6 meses."
            className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
        </div>

        {/* Bloque 2 · aval IADA (mandatory) */}
        <fieldset
          className="p-4 rounded-lg border-2 border-[#F26A21]/40 bg-[#F26A21]/5"
          data-testid="iada-question-fieldset"
        >
          <legend className="px-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F26A21]">
            <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
            ¿Requieres aval académico IADA? *
          </legend>
          <p className="mt-1 text-xs text-gray-700">
            IADA — Instituto de Alta Dirección · Anáhuac Business School respalda académicamente
            los programas designados. El aval incrementa la inversión aproximada en un 30% sobre
            el catálogo base.
          </p>
          <div className="mt-3 grid md:grid-cols-3 gap-2">
            {IADA_OPTIONS.map((opt) => {
              const active = form.wants_iada === opt.val;
              return (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setForm({ ...form, wants_iada: opt.val })}
                  aria-pressed={active}
                  data-testid={opt.testid}
                  className={`text-left p-3 rounded-md border transition-all ${
                    active
                      ? "bg-[#F26A21] border-[#F26A21] text-[#0B0B0B] shadow-md"
                      : "bg-white border-gray-300 hover:border-[#F26A21]/60 text-[#0B0B0B]"
                  }`}
                >
                  <p className="text-sm font-semibold leading-tight">{opt.label}</p>
                  <p className={`mt-1 text-[11px] leading-snug ${active ? "text-[#0B0B0B]/80" : "text-gray-600"}`}>
                    {opt.desc}
                  </p>
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={loading || aiEnabled === false}
            data-testid="ai-generate-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            {aiEnabled === false
              ? "AI assist no disponible"
              : (<><Zap className="w-4 h-4" /> Generar propuesta completa con IA <ArrowRight className="w-4 h-4" /></>)}
          </button>
          <button
            type="button"
            onClick={onSkip}
            data-testid="ai-skip-btn"
            className="text-sm text-gray-600 hover:text-[#0B0B0B] underline underline-offset-4"
          >
            Prefiero componer manualmente
          </button>
        </div>
        {aiEnabled === false && (
          <p className="text-xs text-amber-700">AI assist requiere `EMERGENT_LLM_KEY` en el backend. Puedes continuar manualmente.</p>
        )}
      </form>
    </div>
  );
}

function Field({ label, val, on, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
      <input value={val} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
    </label>
  );
}
function FieldNum({ label, val, on, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
      <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="Ej: 50"
        value={val} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]" />
    </label>
  );
}
function Select({ label, val, on, options, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-600">{label}</span>
      <select value={val} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full mt-1 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-[#0B0B0B] focus:outline-none focus:border-[#F26A21]">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
